import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import {
  clearBooking,
  clearBookingPayment,
  createBookingReservationGroup,
  selectBooking,
  setBookingCreditCardData,
  setBookingRateGroupId,
} from "../features/booking/bookingSlice";
import { selectHotel } from "../features/hotel/hotelSlice";
import { AnimatePresence, motion } from "framer-motion";
import SecureFields from "../components/SecureFields/SecureFields";
import ContentOverlay from "../components/ContentOverlay";
import ECA from "../assets/images/brands/ECA.svg";
import VIS from "../assets/images/brands/VIS.svg";
import IDEAL from "../assets/images/ideal.png";
import GOOGLEPAY from "../assets/images/google-pay.png";
import APPLEPAY from "../assets/images/apple-pay.png";
import { useNavigate } from "react-router-dom";
import {
  getPaymentCardsAll,
  getPaymentCardsAuthorize,
  getReservationGroup,
} from "../features/booking/bookingApi";
import { type IBookingCreditCardData } from "../features/booking/bookingInterface";
import { getUrl, openUrl, returnUrl } from "../helpers/payment";
import { GTMTrackingAddPaymentInfo } from "../helpers/GTMTracking";
import Button from "../components/Button";
import { useTranslation } from "react-i18next";

export default function Payment() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const navigate = useNavigate();

  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [ccOpen, setCcOpen] = useState<boolean>(false);
  const [overlayContent, setOverlayContent] = useState<string>("");
  const { t } = useTranslation();
  const [showDirectPayment, setShowDirectPayment] = useState<boolean>(false);

  useEffect(() => {
    if (booking.Payment.State === "charged") {
      navigate("/success");
    }

    // Get unique RateId from booking reservations
    const uniqueRateIds = Array.from(
      new Set(booking.Reservations.map((reservation) => {
        const rateId = reservation.RateId;
        return hotel.Rates.find((rate) => rate.Id === rateId)?.RateGroupId;
      }))
    );
    const rateGroups = hotel.RateGroups.filter((rateGroup) =>
      uniqueRateIds.includes(rateGroup.Id) && rateGroup.SettlementTrigger === "Confirmation"
    );
    setShowDirectPayment(rateGroups.length === uniqueRateIds.length);
    console.log(rateGroups.length === uniqueRateIds.length);
    


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPaymentState = () => {
    setInterval(() => {
      if (booking.Payment.State === "charged") {
        navigate("/success");
      }
    }, 1000);
  };

  const createNewReservation = async (method: string) => {
    // Set directpayment rate group id to pay with ideal.
    // dispatch(setBookingRateGroupId(hotel.DirectPaymentId));
    setOverlayContent(t("scenes.payment.creatingReservation"));

    // Wait for the dispatch to finish
    await new Promise((r) => setTimeout(r, 1000));

    setShowOverlay(true);
    await dispatch(createBookingReservationGroup({ data: null }))
      .then((response) => {
        if (
          !createBookingReservationGroup.fulfilled.match(response) ||
          !response.payload?.PaymentRequestId
        ) {
          setOverlayContent(
            `[SWW6001] ${t("scenes.payment.somethingWentWrong")}`
          );
          setTimeout(() => {
            setShowOverlay(false);
          }, 2000);
          return;
        }

        setOverlayContent(t("scenes.payment.reservationCreated"));

        // GTM DATALAYER
        GTMTrackingAddPaymentInfo(method);

        setTimeout(() => {
          openUrl(getUrl(method, response.payload.PaymentRequestId));
          setOverlayContent(t("scenes.payment.finishPayment"));
          checkPaymentState();
          return false;
        }, 1000);
      })
      .catch(() => {
        setOverlayContent(
          `[SWW6002] ${t("scenes.payment.somethingWentWrong")}`
        );
        setTimeout(() => {
          setShowOverlay(false);
        }, 2000);
      });
  };

  const showNetworkError = () => {
    setOverlayContent(`[SWW6002] ${t("scenes.payment.somethingWentWrong")}`);
    setShowOverlay(true);
    setTimeout(() => {
      setShowOverlay(false);
    }, 2000);
  };

  const directPayment = async (method: string) => {
    if (booking.Payment.PaymentRequestId !== null) {
      getReservationGroup(hotel, booking)
        .then((response) => {
          const payment = response.data.PaymentRequests.find(
            (p: any) => p.Id === booking.Payment.PaymentRequestId
          );

          if (payment) {
            // create new payment
            if (payment.State === "Expired" || payment.State === "Canceled") {
              // Do nothing, there will be created a new reservation down below
              createNewReservation(method);
            } else {
              // Retry paying
              openUrl(getUrl(method, booking.Payment.PaymentRequestId));
              setOverlayContent(t("scenes.payment.finishPayment"));
              checkPaymentState();
              return false;
            }
          } else {
            createNewReservation(method);
          }
        })
        .catch(() => {
          showNetworkError();
        });
    } else {
      createNewReservation(method);
    }
  };

  const creditCardPayment = async (data: IBookingCreditCardData) => {
    if (data !== undefined) {
      await dispatch(createBookingReservationGroup({ data }))
        .then((response) => {
          if (!createBookingReservationGroup.fulfilled.match(response)) {
            showNetworkError();
            return;
          }

          // Redirect to direct payment
          if (response.payload.PaymentRequestId !== null) {
            openUrl(getUrl("PaymentCard", response.payload.PaymentRequestId));

            // Redirect to credit card authorization
          } else {
            // Set timeout so mews can process the payment card
            setTimeout(() => {
              getPaymentCardsAll(response.payload.PaymentCardId)
                .then((paymentCardResponse) => {
                  const paymentCard =
                    paymentCardResponse.data.PaymentCards.find((pc: any) => {
                      return pc.Id === response.payload.PaymentCardId;
                    });
                  if (paymentCard !== null) {
                    if (paymentCard.AuthorizationState === "Authorizable") {
                      getPaymentCardsAuthorize(
                        hotel,
                        response.payload.PaymentCardId
                      )
                        .then((authorisationResponse) => {
                          // GTM DATALAYER
                          GTMTrackingAddPaymentInfo("Credit Card");

                          if (
                            authorisationResponse.data.State === "Authorized"
                          ) {
                            navigate("/success");
                          } else if (
                            authorisationResponse.data.State === "Pending" ||
                            authorisationResponse.data.State === "Requested"
                          ) {
                            const url =
                              import.meta.env.VITE_MEWS_APPLICATION_URL +
                              "navigator/card-authorization/detail/" +
                              response.payload.PaymentCardId +
                              "?returnUrl=" +
                              returnUrl();
                            openUrl(url);
                            setOverlayContent(
                              t("scenes.payment.finish_authorisation")
                            );
                            return false;
                          }
                        })
                        .catch(() => {
                          setOverlayContent(
                            `[SWW8001] ${t(
                              "scenes.payment.somethingWentWrong"
                            )}`
                          );
                          setShowOverlay(true);
                          setTimeout(() => {
                            setShowOverlay(false);
                          }, 2000);
                        });
                    } else {
                      setOverlayContent(
                        t("scenes.payment.creditcardNotAuthorizable")
                      );
                      setShowOverlay(true);
                      setTimeout(() => {
                        setShowOverlay(false);
                      }, 2000);
                    }
                  }
                })
                .catch(() => {
                  setOverlayContent(
                    `[SWW8002] ${t("scenes.payment.somethingWentWrong")}`
                  );
                  setShowOverlay(true);
                  setTimeout(() => {
                    setShowOverlay(false);
                  }, 2000);
                });
            }, 1000);
          }
        })
        .catch(() => {
          setShowOverlay(true);
          setOverlayContent(
            `[SWW8003] ${t("scenes.payment.somethingWentWrong")}`
          );
          setTimeout(() => {
            setShowOverlay(false);
            dispatch(clearBookingPayment());
            dispatch(clearBooking());
          }, 3000);
        });
    } else {
      setOverlayContent(`[SWW8004] ${t("scenes.payment.somethingWentWrong")}`);
      setTimeout(() => {
        setShowOverlay(false);
      }, 2000);
    }
  };

  const creditCardPaymentSwitch = () => {
    if (booking.Payment.PaymentRequestId !== null) {
      directPayment("PaymentCard");
    } else {
      setCcOpen(!ccOpen);
    }
  };

  interface IFormState {
    Name: string;
    ExpirationDate: string;
    TransactionId: number;
  }

  const getCreditCardData = (creditCardData: IFormState) => {
    // Add zero to month if necessary
    let date = creditCardData.ExpirationDate;
    const dates = date.split("-");
    creditCardData.ExpirationDate =
      dates[0] + "-" + (dates[1].length === 1 ? "0" + dates[1] : dates[1]);

    const data = {
      PaymentGatewayData: creditCardData.TransactionId,
      Expiration: creditCardData.ExpirationDate,
      HolderName: creditCardData.Name,
    };

    setOverlayContent(t("scenes.payment.creatingReservation"));
    setShowOverlay(true);

    dispatch(setBookingCreditCardData(data));
    dispatch(setBookingRateGroupId(""));

    creditCardPayment(data);
  };

  return (
    <>
      <div className="absolute flex flex-col w-full h-full">
        <div className="flex-grow relative w-full">
          <motion.div
            initial={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
            animate={{ opacity: 1, y: "0px", transition: { duration: 0.4 } }}
            exit={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
            className="absolute inset-0 overflow-y-auto w-full px-4 py-2 pb-4"
          >
            <div className="flex flex-col">
              <div className="text-fluid-h3">
                {t("scenes.payment.paymentMethods")}
              </div>
              <p className="ll">
                {t("scenes.payment.paymentMethodsDescription")}
              </p>
              {hotel.PaymentGateway &&
                hotel.PaymentGateway.PaymentGatewayType === "PciProxy" && (
                  <div className="bg-custom-100 rounded-lg px-6 py-4 mt-4">
                    <AnimatePresence>
                      {ccOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="text-h4">
                            {t("scenes.payment.authorizeCreditCard")}
                          </div>

                          <div className="mt-2">
                            <SecureFields
                              production={
                                import.meta.env.VITE_ENV === "production"
                              }
                              config={{
                                merchantID: hotel.PaymentGateway.PublicKey,
                                fields: {
                                  cardNumber: {
                                    placeholderElementId: "card-number",
                                    inputType: "tel",
                                  },
                                  cvv: {
                                    placeholderElementId: "cvv-number",
                                    inputType: "tel",
                                  },
                                },
                                options: {
                                  paymentMethods: ["ECA", "VIS"],
                                },
                              }}
                              callback={getCreditCardData}
                            />
                          </div>
                          <div className="mt-4 text-center">
                            <span>{t("scenes.payment.cardChargeInfo")}</span><br />
                            <span>{t("scenes.payment.securedWithMews")}</span>
                            <div className="flex justify-center gap-2 mt-2">
                              <img src={VIS} alt="Visa" />
                              <img src={ECA} alt="Master Card" />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <AnimatePresence>
                      {!ccOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="text-h4">
                            {t("scenes.payment.payWithCreditCard")}
                          </div>
                          <div className="flex gap-2 my-2 items-center">
                            <div className="p-2 bg-white rounded-md">
                              <img src={VIS} alt="Visa" className="h-fluid-6" />
                            </div>
                            <div className="p-2 bg-white rounded-md">
                              <img
                                src={ECA}
                                alt="Master Card"
                                className="h-fluid-6"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              size="small"
                              text={t("scenes.payment.payWithCreditCard")}
                              onClick={() => creditCardPaymentSwitch()}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
            </div>

            {showDirectPayment && (
              <div className="bg-custom-100 bg-opacity-50 rounded-lg px-6 py-4 mt-6">
                <div className="text-h4">{t("scenes.payment.payDirectly")}</div>
                <div className="flex gap-2 my-2">
                  <div className="p-2 bg-white rounded-md">
                    <img src={IDEAL} alt="Ideal" className="h-fluid-6" />
                  </div>
                  <div className="p-2 bg-white rounded-md">
                    <img src={GOOGLEPAY} alt="Google pay" className="h-fluid-6" />
                  </div>
                  <div className="p-2 bg-white rounded-md">
                    <img src={APPLEPAY} alt="Apple pay" className="h-fluid-6" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    size="small"
                    text={t("scenes.payment.payDirectly")}
                    onClick={() => directPayment("Ideal")}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <ContentOverlay show={showOverlay} content={overlayContent} />
    </>
  );
}
